import { Link } from "react-router-dom";
import { fmtDate, inr } from "@/lib/format";

const n = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

function ItemTable({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 overflow-x-auto bg-white rounded border border-violet-100">
      <table className="w-full text-[11px]" data-testid="lm-projection-items">
        <thead><tr className="text-left text-violet-800">{["Item", "Projected qty", "Filled qty", "Open qty", "Projected value", "Filled value", "Open value"].map((h) => <th key={h} className="px-2 py-1 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{items.map((it) => { const extra = !it.proj_qty && !it.proj_value; return (
          <tr key={`${it.item_code || ""}|${it.description || ""}|${it.uom || ""}`} className="border-t border-violet-50">
            <td className="px-2 py-1"><span className="font-mono">{it.item_code}</span> {it.description}</td>
            <td className="px-2 py-1 font-mono">{extra ? "—" : `${n(it.proj_qty)} ${it.uom}`}</td><td className="px-2 py-1 font-mono">{n(it.filled_qty)}</td>
            <td className={`px-2 py-1 font-mono ${!extra && it.open_qty < 0 ? "text-amber-700" : ""}`}>{extra ? "—" : n(it.open_qty)}</td>
            <td className="px-2 py-1 font-mono">{extra ? "—" : inr(it.proj_value)}</td><td className="px-2 py-1 font-mono">{inr(it.filled_value)}</td>
            <td className={`px-2 py-1 font-mono ${!extra && it.open_value < 0 ? "text-amber-700" : ""}`}>{extra ? "—" : inr(it.open_value)}</td>
          </tr>); })}</tbody>
      </table>
    </div>
  );
}

function Balance({ o, s }) {
  const pct = Math.max(0, Math.min(100, s.filled_pct || 0));
  return (
    <div className="mb-3 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900" data-testid="lm-projection-banner">
      <div>LMPP projection ({o.status.replace(/_/g, " ")}){o.expected_po_date ? ` - PO expected ${fmtDate(o.expected_po_date)}` : ""}. Never dispatched or invoiced; confirmed LMs are booked against it and fill it up.</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono" data-testid="lm-projection-balance">
        <span>Value <b>{inr(s.value)}</b></span><span>Filled <b>{inr(s.consumed)}</b>{s.filled_pct != null ? ` (${s.filled_pct}%)` : ""}</span>
        <span className={s.balance < 0 ? "text-amber-700" : ""}>Open <b>{inr(s.balance)}</b></span>
        {s.in_draft > 0 && <span className="text-slate-500" data-testid="lm-projection-draft">+ {inr(s.in_draft)} in draft LMs (not counted until submitted)</span>}
        {o.short_closed && <span>short-closed by {o.short_closed.by?.name}: {o.short_closed.reason}</span>}
      </div>
      <div className="mt-1.5 h-2 rounded bg-violet-100 overflow-hidden" data-testid="lm-projection-progress"><div className="h-full bg-violet-600" style={{ width: `${pct}%` }} /></div>
      <ItemTable items={s.items} />
      {s.linked.length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-testid="lm-projection-linked">Filled by: {s.linked.map((x) => <Link key={x.id} to={`/orders/${x.id}`} className={`underline ${x.counted ? "" : "text-slate-500"}`}>{x.order_no} ({x.status.replace(/_/g, " ")}, {inr(x.subtotal)}{x.counted ? "" : ", not counted"})</Link>)}</div>}
      {s.linked.length === 0 && <div className="mt-2 text-violet-700">No confirmed LM booked against this projection yet.</div>}
    </div>
  );
}

// Projection LM: value, filled by linked confirmed LMs (value + item-wise qty), open balance. Confirmed LM: which LMPP it fills.
export function ProjectionInfo({ o }) {
  const s = o.projection_summary;
  const l = o.projection_link;
  if (s) return <Balance o={o} s={s} />;
  if (!l) return null;
  const over = (o.subtotal || 0) > l.balance_before + 0.5;
  return (
    <div className={`mb-3 rounded border px-3 py-2 text-xs ${over ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="lm-projection-link-banner">
      Booked against projection <Link to={`/orders/${l.id}`} className="underline font-mono">{l.order_no}</Link> ({l.status.replace(/_/g, " ")}) — balance before this LM {inr(l.balance_before)} of {inr(l.value)}.
      {over && <b data-testid="lm-projection-overbooked"> This LM is {inr((o.subtotal || 0) - l.balance_before)} above the balance; on approval the projection closes anyway.</b>}
    </div>
  );
}
